import { useCallback, useEffect, useRef, useState } from "react";
import type { FopConfig, FreqPoint } from "../types/dashboard";
import { normalizeFopHz } from "../utils/freqHz";
import { formatTsMs } from "../utils/formatTsMs";

const MAX_FREQ_POINTS = 2000;

function normalizePoint(
  raw: Partial<FreqPoint> & { fop_hz?: number | null },
): FreqPoint | null {
  const ts_ms = Number(raw.ts_ms);
  const freq_hz = Number(raw.freq_hz);
  if (!Number.isFinite(ts_ms) || !Number.isFinite(freq_hz)) return null;
  const fop_hz = normalizeFopHz(raw.fop_hz);
  return {
    ts_ms,
    freq_hz,
    fop_hz,
    raw_u16: Number.isFinite(raw.raw_u16) ? Number(raw.raw_u16) : 0,
  };
}

function appendPoint(history: FreqPoint[], point: FreqPoint): FreqPoint[] {
  const last = history[history.length - 1];
  if (last?.ts_ms === point.ts_ms) {
    if (last.freq_hz === point.freq_hz) return history;
    return [...history.slice(0, -1), point];
  }
  if (last && point.ts_ms < last.ts_ms) return history;
  return [...history, point].slice(-MAX_FREQ_POINTS);
}

export type MqttFreqState = {
  freqLive: FreqPoint | null;
  freqHistory: FreqPoint[];
  fopConfig: FopConfig | null;
  latestSampleMeta: string | null;
  mainSyncStatus: string | null;
  regionsSyncStatus: string | null;
  regionSyncById: Record<string, boolean>;
  mqttConnected: boolean;
  error: string | null;
  applyBootstrap: (points: FreqPoint[]) => void;
};

export function useMqttFreq(active: boolean): MqttFreqState {
  const [freqLive, setFreqLive] = useState<FreqPoint | null>(null);
  const [freqHistory, setFreqHistory] = useState<FreqPoint[]>([]);
  const [fopConfig, setFopConfig] = useState<FopConfig | null>(null);
  const [latestSampleMeta, setLatestSampleMeta] = useState<string | null>(null);
  const [mainSyncStatus, setMainSyncStatus] = useState<string | null>(null);
  const [regionsSyncStatus, setRegionsSyncStatus] = useState<string | null>(null);
  const [regionSyncById, setRegionSyncById] = useState<Record<string, boolean>>({});
  const [mqttConnected, setMqttConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const esRef = useRef<EventSource | null>(null);

  const applyBootstrap = useCallback((points: FreqPoint[]) => {
    if (!points.length) return;
    const sorted = [...points].sort((a, b) => a.ts_ms - b.ts_ms);
    const last = sorted[sorted.length - 1]!;
    setFreqHistory(sorted.slice(-MAX_FREQ_POINTS));
    setFreqLive(last);
    setLatestSampleMeta(formatTsMs(last.ts_ms));
  }, []);

  useEffect(() => {
    if (!active) {
      setFreqLive(null);
      setFreqHistory([]);
      setFopConfig(null);
      setLatestSampleMeta(null);
      setMainSyncStatus(null);
      setRegionsSyncStatus(null);
      setRegionSyncById({});
      setMqttConnected(false);
      setError(null);
      esRef.current?.close();
      esRef.current = null;
      return;
    }

    setError(null);
    const es = new EventSource("/api/freq/live");
    esRef.current = es;

    es.addEventListener("control", (ev) => {
      try {
        const data = JSON.parse(ev.data) as {
          type?: string;
          mqtt_connected?: boolean;
          error?: string;
        };
        if (data.type === "subscribed") {
          setMqttConnected(Boolean(data.mqtt_connected));
          setError(null);
        } else if (data.type === "mqtt_closed") {
          setMqttConnected(false);
        } else if (data.type === "error") {
          setError(data.error ?? "MQTT error");
        }
      } catch {
        /* ignore */
      }
    });

    es.addEventListener("sample", (ev) => {
      try {
        const raw = JSON.parse(ev.data) as Partial<FreqPoint> & {
          sync_status?: string;
          fop_hz?: number | null;
        };
        const point = normalizePoint(raw);
        if (!point) return;
        setFreqLive(point);
        setLatestSampleMeta(formatTsMs(point.ts_ms));
        setFreqHistory((prev) => appendPoint(prev, point));
        if (raw.sync_status) setMainSyncStatus(String(raw.sync_status));
        setMqttConnected(true);
        setError(null);
      } catch {
        /* ignore */
      }
    });

    es.addEventListener("fop_config", (ev) => {
      try {
        const data = JSON.parse(ev.data) as FopConfig;
        if (Number.isFinite(data.insensitivity_range_mhz)) {
          setFopConfig(data);
        }
      } catch {
        /* ignore */
      }
    });

    es.addEventListener("regions_sync", (ev) => {
      try {
        const data = JSON.parse(ev.data) as {
          status?: string;
          regions?: Record<string, boolean>;
        };
        if (data.status) setRegionsSyncStatus(String(data.status));
        if (data.regions) setRegionSyncById(data.regions);
      } catch {
        /* ignore */
      }
    });

    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) {
        setMqttConnected(false);
        setError((prev) => prev ?? "freq stream closed");
      }
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [active]);

  return {
    freqLive,
    freqHistory,
    fopConfig,
    latestSampleMeta,
    mainSyncStatus,
    regionsSyncStatus,
    regionSyncById,
    mqttConnected,
    error,
    applyBootstrap,
  };
}
